import { NetworkStatus } from '@apollo/client'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BackgroundBox from '../../components/custom_components/BackgroundBox'
import { ContentContainer } from '../../components/custom_components/ContentContainer'
import { KudosFilterInput, RoleName } from '../../types'
import { useAllAttentionPoints } from '../../graphql/kudos/useKudosQueries'
import { useCollapse } from '../../routes/collapse-context'
import PerSpacer from '../../components/custom_components/Spacer'
import { KUDOS_DASHBOARD_PAGE_SIZE } from '../../graphql/kudos/util'
import { AttentionPointTable } from './components/AttentionPointTable'
import AttentionPointInfoBox from './components/AttentionPointInfoBox'
import { NewLoadingAndErrors } from '../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { useAuth } from '../../context/auth-context'
import { ShowMoreButton } from '../reports/table/ShowMoreButton'

export const AttentionPointManagementPage: FC = () => {
  const { user } = useAuth()
  const collapsed = useCollapse()
  const { t } = useTranslation()

  if (!user.roles || user.roles.length === 0 || !user.roles.includes(RoleName.Hr)) {
    return (
      <ContentContainer collapsed={collapsed}>
        <BackgroundBox r height="fullPage">
          <NewLoadingAndErrors
            isLoading={false}
            error={undefined}
            customErrorMessage={t('page.pageUnavailable.customMessage.KudosManagementPage')}
            hasNoData={false}
            showSpinner
            t={t}
          />
        </BackgroundBox>
      </ContentContainer>
    )
  } else return <AttentionPointManagementPageContent />
}

export const AttentionPointManagementPageContent: FC = () => {
  const { t } = useTranslation()

  const collapsed = useCollapse()
  const [filters, setFilters] = useState<KudosFilterInput>({
    pagination: {
      offset: 0,
      limit: KUDOS_DASHBOARD_PAGE_SIZE,
    },
  })
  const [firstLoadingDone, setFirstLoadingDone] = useState(false)

  const { data, loading, error, fetchMore, networkStatus } = useAllAttentionPoints(filters)

  useEffect(() => {
    if (!!data?.getAllAttentionPoints) {
      setFirstLoadingDone(true)
    }
  }, [data])

  const handleFiltersChange = (newFilters: KudosFilterInput) => {
    setFilters(newFilters)
  }

  const loadMore = () => {
    if (data && data.getAllAttentionPoints.hasMore) {
      fetchMore({
        variables: {
          filters: {
            ...filters,
            pagination: {
              offset: data.getAllAttentionPoints.nextOffset || 0,
              limit: KUDOS_DASHBOARD_PAGE_SIZE,
            },
          },
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousResult
          }

          const previousAttentionPoints = previousResult.getAllAttentionPoints.kudos
          const fetchMoreAttentionPoints = fetchMoreResult.getAllAttentionPoints.kudos

          fetchMoreResult.getAllAttentionPoints.kudos = [...previousAttentionPoints, ...fetchMoreAttentionPoints]

          return { ...fetchMoreResult }
        },
      })
    }
  }

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore
  const showMoreLayoutActive = !!data?.getAllAttentionPoints?.hasMore && (!loading || isLoadingMore)

  if ((error || loading) && !firstLoadingDone) {
    return (
      <ContentContainer collapsed={collapsed}>
        <BackgroundBox r height="fullPage">
          <NewLoadingAndErrors isLoading={loading} error={error} hasNoData={false} showSpinner t={t} />
        </BackgroundBox>
      </ContentContainer>
    )
  }

  return (
    <ContentContainer collapsed={collapsed}>
      <AttentionPointInfoBox filters={filters} onFiltersChange={handleFiltersChange} />
      <PerSpacer mb="xs" />
      <AttentionPointTable
        attentionPoints={data?.getAllAttentionPoints?.kudos}
        loading={loading}
        isLoadingMore={isLoadingMore}
        error={error}
        filters={filters}
        showMoreButtonVisible={showMoreLayoutActive}
      />

      <ShowMoreButton visible={showMoreLayoutActive} loadingMore={isLoadingMore} onLoadMore={loadMore} />
    </ContentContainer>
  )
}
